import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { keycloak } from '../lib/keycloak';
import api from '../lib/api';

interface User {
  id: string;
  keycloakSub: string | null;
  email: string | null;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Keycloak is already initialized (login-required ensured auth before render)
    api
      .get('/auth/me')
      .then((res) => setUser(res.data))
      .catch((err) => {
        // Only redirect on explicit auth rejections, not transient server/network errors.
        // Blindly calling keycloak.login() on any failure creates an infinite redirect loop
        // if the backend is temporarily unavailable or misconfigured.
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          keycloak.login();
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  const logout = useCallback(() => {
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: keycloak.authenticated ?? false,
        isLoading,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
